<template>
  <!-- ============================================================
       会议室订单卡片（全站统一 · 销售端方案）
       ------------------------------------------------------------
       来源：pages/client.vue「会议室订单」列表卡片 roomorder-item 1:1 复刻。
       为什么抽成组件：运营/市场/财务三端此前各有一套 bill-card / room-card，
         同一条订单在不同端长得不一样，用户要重新认一遍字段位置。
       为什么类名与销售端完全一致（crm-item / roomorder-item / customer-card* /
         status-tag / tag-* / btn-action / item-detail / info-row / info-col）：
         这些是 client.vue 里**非 scoped** 的全局样式（router 为静态 import，
         全站 CSS 打成一个包），直接复用即可视觉 1:1，无需抄第二份 CSS。
       ⚠️ 三条硬约束：
         1) 左侧 4px 蓝条**不要**（用户 10-22 明确要求去掉）：.bill-list .crm-item 本身
            已置 border-left:none（P111 去双色条），这里再显式兜一层，避免组件被搬到
            .bill-list 之外时又冒出蓝条。
         2) 金额脱敏（运营端 P169-B）时不下发 amount/price，卡片自动省掉价格段，
            绝不出现「¥—」这种半截文案。
         3) 卡片主体可点 → 抛 detail 事件（运营端详情弹层里的「推送待办/完成服务」
            依赖这条链路，不能被卡片改版切断）。
       ============================================================ -->
  <div class="crm-item roomorder-item room-order-card">
    <div class="item-head customer-card roomorder-card">
      <div class="customer-card__main">
        <div class="customer-card__content"
             :class="{ 'room-order-card__content--tap': clickable }"
             @click="clickable && $emit('detail', item)">
          <!-- 【P418-c】标题独占整行、全部显示（不省略不截断，长名字自动换行）
               按「【…】」段折行：段内不拆（日期 2026-10-22 不会被断成 2026- / 10-22），
               段之间用 <wbr> 提供断点 ⇒ 换行整齐且信息不残缺。 -->
          <div class="room-order-card__title">
            <template v-for="(seg, si) in titleSegs">
              <span class="room-order-card__seg" :key="'sg' + si">{{ seg }}</span><wbr :key="'wb' + si">
            </template>
          </div>

          <!-- 【P418-c】状态/属性标签统一独立成行，与标题分开；
               同字标签只显示一次（详见 computed 里的去重注释） -->
          <div class="room-order-card__tags" v-if="hasTags">
            <span class="status-tag tag-allot" v-if="timeTag">{{ timeTag }}</span>
            <span class="status-tag" :class="freeCls">{{ freeText }}</span>
            <span class="status-tag" :class="statusCls" v-if="statusText">{{ statusText }}</span>
            <span class="status-tag" :class="apprCls" v-if="apprText">{{ apprText }}</span>
          </div>

          <div class="customer-card__row">客户：{{ customerName }} · 门店：{{ storeName }}</div>
          <div class="customer-card__row customer-card__row--muted">
            会议室：{{ roomName }}<template v-if="!masked && priceLine"> · {{ priceLine }}</template>
          </div>
        </div>
      </div>

      <div class="customer-card__actions">
        <div class="btn-action btn-action--primary"
             v-if="canEdit"
             :class="{ 'is-disabled': editDisabled }"
             @click.stop="editDisabled ? null : $emit('edit', item)">✎ 编辑</div>
        <div class="btn-action btn-action--danger" v-if="canDelete" @click.stop="$emit('delete', item)">🗑 删除</div>
        <div class="btn-action btn-action--secondary" @click.stop="open = !open">
          <span>{{ open ? '▲ 收起' : '▼ 更多' }}</span>
        </div>
      </div>
    </div>

    <!-- 展开区：次要字段（销售端 item-detail / info-row / info-col 同款） -->
    <div v-if="open" class="item-detail">
      <div class="info-row">
        <div class="info-col"><span class="label">使用日期：</span><span>{{ dayText }}</span></div>
        <div class="info-col"><span class="label">时段：</span><span>{{ timeTypeRaw || '—' }}</span></div>
        <div class="info-col"><span class="label">使用时间：</span><span>{{ timeRange }}</span></div>
        <div class="info-col"><span class="label">人数：</span><span>{{ peopleText }}</span></div>
        <div class="info-col"><span class="label">编号：</span><span>{{ serialText }}</span></div>
        <div class="info-col info-col1" v-if="remarkText"><span class="label">备注：</span><span>{{ remarkText }}</span></div>
      </div>
    </div>
  </div>
</template>

<script>
/* ============================================================================
 * RoomCard —— 会议室订单卡片（销售端 roomorder-item 方案的公共实现）
 * ----------------------------------------------------------------------------
 * props:
 *   item      行数据（BillListService 的 room 行）
 *   masked    true = 金额脱敏（运营端），不渲染「销售价/市场价」
 *   canEdit / canDelete  行级操作权限（后端 can_edit / can_delete）
 * emits: detail(item) / edit(item) / delete(item)
 * ========================================================================== */
function money(v) {
  const n = Number(v)
  if (!isFinite(n)) return ''
  return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export default {
  name: 'RoomCard',
  props: {
    item: { type: Object, default: () => ({}) },
    masked: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false },
    /* 编辑入口置灰（销售端「审核中不可修改」）：只影响视觉与点击，
       后端 save 仍有 403 硬拦截，两层防护。
       样式复用 client.vue 全局的 .btn-action.is-disabled（opacity .5 + pointer-events:none）。 */
    editDisabled: { type: Boolean, default: false },
    /* 卡片主体是否可点开详情。市场端会议室列表没有详情页 ⇒ 传 false，
       不绑事件也不显示「可点」手型，避免点了没反应。 */
    clickable: { type: Boolean, default: true }
  },
  data() {
    return { open: false }
  },
  computed: {
    it() { return this.item || {} },
    nm() { return this.it.name || this.it.title_text || this.it.serial_number || '—' },
    /** 标题分段：订单名是「【门店】【会议室】【日期】【时段】」四段拼接。
     *  拆成段后每段 white-space:nowrap（日期不被拆开），段间插 <wbr> 作为唯一断点。
     *  非该格式（或拼不出完整字符串）时退回整段，仍靠 overflow-wrap 保证完整显示。 */
    titleSegs() {
      const s = this.nm || ''
      const m = s.match(/【[^】]*】/g)
      if (!m || m.join('') !== s) return [s]
      return m
    },
    serialText() { return this.it.serial_number || '—' },
    dayText() { return (this.it.on_site_time || '').slice(0, 10) || '—' },
    peopleText() {
      const v = this.it.number_people
      return (v === undefined || v === null || v === '') ? '—' : v
    },
    remarkText() { return this.it.remark || '' },
    customerName() { return this.it.customer_name || '—' },
    /* 【P419】门店字段两端不同名：BillList 引擎（client/market/operation）下发 store_name，
       财务端 roomOrdersList 只下发 stores_name（实测 store_name 为 null）⇒ 必须兼容，
       否则财务端卡片会退化成「门店：—」，与标题里的【门店】段对不上。 */
    storeName() { return this.it.store_name || this.it.stores_name || '—' },
    roomName() { return this.it.meeting_room_name || this.it.room_name || '—' },
    /* 时段文案：后端 maps 产出 time_type_name（销售端字段名是 time_type_text，两个都兼容） */
    timeTypeRaw() { return this.it.time_type_text || this.it.time_type_name || '' },
    /* 【P418-c】时段标签：订单标题本身已含「【全天】」之类的时段段位 ⇒ 不再重复打一个标签。
       只影响标签，展开区「时段：」仍用 timeTypeRaw，不受影响。 */
    timeTag() {
      const t = this.timeTypeRaw
      if (!t) return ''
      return this.nm.indexOf(t) >= 0 ? '' : t
    },
    /* 使用时间：start_time 历史上存过 'HH:mm-HH:mm' 文本，也存过 '["08:30","20:30"]' JSON 数组 */
    timeRange() {
      const raw = this.it.start_time_range || this.it.start_time_text || this.it.start_time
      if (raw === undefined || raw === null || raw === '') return '—'
      let s = String(raw).trim()
      if (s.charAt(0) === '[') {
        try {
          const arr = JSON.parse(s)
          if (Array.isArray(arr)) return arr.slice(0, 2).join('~') || '—'
        } catch (e) { /* 落到下面按文本处理 */ }
      }
      return s.replace(/-/g, '~') || '—'
    },
    priceLine() {
      const a = Number(this.it.amount)
      const p = Number(this.it.price)
      const seg = []
      if (isFinite(a) && a > 0) seg.push('销售价：¥' + money(a))
      if (isFinite(p) && p > 0) seg.push('市场价：¥' + money(p))
      /* 后端脱敏后 amount/price 为空串 → Number('')=0，天然被过滤掉 */
      return seg.join(' · ')
    },
    /* 订单状态：0 审核中 / 1 通过 / 2 驳回（销售端 status_text 口径） */
    statusText() { return this.it.status_text || this.it.status_name || '' },
    statusCls() {
      const s = Number(this.it.status)
      if (s === 0) return 'tag-wait'
      if (s === 2) return 'tag-danger'
      return 'tag-receive'
    },
    freeText() {
      if (this.it.is_free_text) return this.it.is_free_text
      return Number(this.it.is_free) === 2 ? '免费' : '收费'
    },
    freeCls() { return Number(this.it.is_free) === 2 ? 'tag-free' : 'tag-charge' },
    /* 审批状态：与销售端 roomOrderApprovalClass 同一映射
       （0未开始/1执行中/2等待审核 → 橙；3通过 → 绿；5未通过/6拒绝 → 红；其余 → 灰）
       【P418-c】★ 去重：订单状态与审批状态同字时（最常见就是两个都显示「通过」），
       只保留订单状态那一个 —— 用户看到两个「通过」会以为重复渲染了。 */
    apprText() {
      const t = this.it.approval_status_name || ''
      if (!t) return ''
      return (this.statusText && t === this.statusText) ? '' : t
    },
    apprCls() {
      const s = Number(this.it.approval_status)
      if (s === 3) return 'tag-receive'
      if (s === 5 || s === 6) return 'tag-danger'
      if (s === 0 || s === 1 || s === 2) return 'tag-wait'
      return 'tag-allot'
    },
    /** 标签行是否有内容（全为空则整行不渲染，避免留一条空白） */
    hasTags() {
      return !!(this.timeTag || this.freeText || this.statusText || this.apprText)
    }
  }
}
</script>

<style scoped>
.room-order-card {
  border-left: none !important;
}
/* 卡片主体可点击 → 给一点按压反馈，避免用户不知道能点开详情 */
.room-order-card__content--tap {
  cursor: pointer;
}
/* ============================================================
 * 【P418-c】标题独占整行并全部显示
 *   全局 .customer-card__name 是「单行 + 省略号」（white-space:nowrap;
 *   overflow:hidden; text-overflow:ellipsis），订单名是四段拼接
 *   （门店/会议室/日期/时段），一定被截断 ⇒ 用户看不到完整标题。
 *   这里改用独立标题节点 + 允许换行，长名字自然折行，不截断。
 * ============================================================ */
.room-order-card__title {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
  line-height: 1.5;
  white-space: normal;
  word-break: normal;
  overflow-wrap: anywhere;
}
/* 每段「【…】」整体不拆行（避免 2026-10-22 被断成 2026- / 10-22） */
.room-order-card__seg {
  white-space: nowrap;
}
/* 标签行：与标题分开独立一行，自动换行不挤压 */
.room-order-card__tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}
.room-order-card__tags .status-tag {
  margin: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/RoomCard.vue